import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useAsyncResource, UnauthorizedError } from '../../composables/useAsyncResource'
import { apiMutate } from '../../composables/apiMutate'
import { useChannelSubscription, type ChannelSubscription } from '../../composables/useChannelSubscription'
import { useAgentRunIndicator } from './runIndicator'
import { useAgentLiveSync } from './liveSync'
import { fetchThreadStatus, fetchTranscript } from './api'
import { userTurnFromBroadcast } from './userTurn'
import type { AgentSessionSummary, ChatMessage, SessionsResponse, CreateResponse, OutputBroadcast } from './types'

export type { AgentSessionSummary, ChatMessage }

// Backs the admin-only /agent page. Three parts:
//
//   - the cross-channel session list (GET /api/v1/agent/sessions), via
//     useAsyncResource for the standard loaded/loading/needsAuth lifecycle.
//   - sending messages (POST /api/v1/agent/messages) through the brain proxy
//     to the agent box; a message with no active thread starts a new web
//     session and adopts the server-assigned `web-<uuid>` thread id.
//   - transcript: opening a session loads its persisted history
//     (GET /api/v1/agent/sessions/:thread_id/messages) so a refresh restores
//     the conversation, then subscribes to AgentOutputChannel for live output
//     going forward.
//
// The local run-indicator state machine (agentRunning/liveStatus) lives in
// ./runIndicator, and the two live ActionCable subscriptions live in
// ./liveSync (#835) — this file wires them into the transcript/session-list
// flow above.
export const useAgentStore = defineStore('agent', () => {
  const {
    data: sessionsData,
    loaded,
    loading,
    error: loadError,
    needsAuth,
    refresh: fetchSessions,
  } = useAsyncResource<SessionsResponse>(
    async () => {
      const res = await fetch('/api/v1/agent/sessions', { credentials: 'include' })
      if (res.status === 401) throw new UnauthorizedError()
      if (!res.ok) throw new Error(`Failed to load sessions: ${res.status}`)
      return res.json()
    },
    { initialValue: { sessions: [] } },
  )

  const activeThreadId = ref<string | null>(null)
  const messages = ref<ChatMessage[]>([])
  let subscription: ChannelSubscription | null = null

  const { agentRunning, liveStatus, startRunning, stopRunning, pauseRunningIndicator, setProgress } =
    useAgentRunIndicator(activeThreadId, (threadId) => fetchThreadStatus(threadId).then((s) => s?.running ?? false))

  // One live subscription at a time — the session you're currently viewing.
  // Parameterised by thread_id so switching sessions opens a distinct
  // subscription (the once-guard keys on channel + params); migrated onto
  // useChannelSubscription to inherit reconnect recovery.
  function subscribe(threadId: string) {
    subscription?.unsubscribe()
    subscription = useChannelSubscription<OutputBroadcast>(
      { channel: 'AgentOutputChannel', params: { thread_id: threadId } },
      {
        received(data) {
          if (data.thread_id !== activeThreadId.value) return
          if (data.event_type === 'progress') {
            // Progress events update the live status bubble in place; no
            // message is appended until the final broadcast lands.
            setProgress(data.thread_id, data.output)
          } else if (data.event_type === 'user') {
            // The user's own turn (#1650) — how a second tab/device watching
            // this thread sees a message typed elsewhere, and (since
            // `sendMessage` no longer pushes optimistically) how the sending
            // tab renders its own bubble too.
            messages.value.push(userTurnFromBroadcast(data))
            // A channel turn's channel name only comes from the transcript.
            if (data.channel_message_id) void reloadTranscript(threadId)
          } else {
            // 'final' or absent (older-server compat): one complete agent turn.
            messages.value.push({
              role: 'agent',
              text: data.output,
              created_at: new Date().toISOString(),
              context_tokens: data.context_tokens,
              model: data.model,
              input_tokens: data.input_tokens,
              output_tokens: data.output_tokens,
              cache_read_tokens: data.cache_read_tokens,
              cache_creation_tokens: data.cache_creation_tokens,
            })
            stopRunning()
          }
        },
        refetchOnReconnect() {
          // A transient WS drop can lose broadcasts emitted while we were
          // disconnected (a run can last minutes); re-pull the persisted
          // transcript so nothing is silently dropped mid-run.
          void reloadTranscript(threadId)
          // Sync running state with server truth after a WS drop: a run that
          // finished while disconnected must clear agentRunning (its final
          // broadcast was missed); a run still in flight must restore it if
          // the local timer fired while disconnected.
          void fetchThreadStatus(threadId).then((status) => {
            if (activeThreadId.value !== threadId) return
            if (status?.running) startRunning()
            else stopRunning()
          })
        },
      },
    )
  }

  // Switch the active thread: reset the transcript + (re)subscribe for live
  // output. Subscribe before any await so no broadcast is missed in the gap.
  function selectThread(threadId: string) {
    activeThreadId.value = threadId
    messages.value = []
    pauseRunningIndicator()
    subscribe(threadId)
  }

  // Reset to the new-session form: drop the live subscription and the
  // active thread so the start form (with the repo field) returns.
  function newSession() {
    subscription?.unsubscribe()
    subscription = null
    activeThreadId.value = null
    messages.value = []
    pauseRunningIndicator()
  }

  // Open an EXISTING session (sidebar click / deep link): select it, then load
  // its persisted transcript so a refresh or a session started elsewhere shows
  // history. Loaded history is prepended under whatever streamed in meanwhile.
  // Also restores the "Working…" indicator if the server reports the run is
  // still in flight.
  async function openSession(threadId: string): Promise<void> {
    selectThread(threadId)
    const [history, status] = await Promise.all([fetchTranscript(threadId), fetchThreadStatus(threadId)])
    if (activeThreadId.value !== threadId) return // user switched away
    if (history) messages.value = [...history, ...messages.value]
    if (status?.running) {
      // TOCTOU guard: a status broadcast for running: false may have arrived
      // while the HTTP fetch was in flight, updating sessionsData already. If
      // so, skip startRunning() — the broadcast already settled the state and
      // no further signal will arrive to clear it.
      const sess = sessionsData.value.sessions.find((s) => s.thread_id === threadId)
      if (sess?.running !== false) startRunning()
    }
  }

  // Reconnect recovery: replace the transcript with the authoritative persisted
  // history (every turn is persisted server-side), recovering any broadcast
  // missed during a WS drop. Guarded so a late reconnect for a thread the user
  // has since switched away from can't clobber the current transcript.
  async function reloadTranscript(threadId: string): Promise<void> {
    const history = await fetchTranscript(threadId)
    if (history && activeThreadId.value === threadId) {
      messages.value = history
    }
  }

  // Start a new web session: relay the first message (optionally targeting a
  // repo), adopt the server-assigned thread id, subscribe for streamed output.
  async function startSession(message: string, repo: string): Promise<void> {
    const body: Record<string, unknown> = { message }
    if (repo.trim()) body.repo = repo.trim()
    const res = await apiMutate<CreateResponse>('/api/v1/agent/messages', {
      method: 'POST',
      body,
      failureLabel: 'Failed to start session',
    })
    if (!res?.thread_id) throw new Error('No thread id returned')
    // New session: no history to load — show the prompt optimistically and
    // refresh the list so the session appears in the sidebar immediately
    // (the user turn is already persisted server-side by the POST above).
    // Still pushed optimistically rather than left to the live broadcast
    // (#1650): `selectThread` only subscribes once the thread id is known,
    // after this same POST already resolved and the server already
    // broadcast — too late for this tab to catch its own first turn. A
    // brand-new thread has no other viewer yet either, so there's no gap.
    selectThread(res.thread_id)
    messages.value.push({ role: 'you', text: message, created_at: new Date().toISOString() })
    startRunning()
    await fetchSessions()
  }

  // Continue the active session — relays under the existing thread id. No
  // optimistic local push (#1650): the subscription is already live, so the
  // 'user' broadcast handled in `subscribe` above renders this tab's own
  // bubble too, the same source every other viewer of this thread uses.
  async function sendMessage(message: string, postToChannel = false): Promise<void> {
    const threadId = activeThreadId.value
    if (!threadId) return
    // Start the indicator before awaiting so a final broadcast that arrives
    // during the POST (fast agent) calls stopRunning() AFTER startRunning()
    // rather than before — preventing the indicator from being re-raised after
    // the broadcast has already cleared it.
    startRunning()
    try {
      await apiMutate('/api/v1/agent/messages', {
        method: 'POST',
        body: { thread_id: threadId, message, post_to_channel: postToChannel || undefined },
        failureLabel: 'Failed to send message',
      })
    } catch (e) {
      stopRunning()
      throw e
    }
  }

  // Rename a session: persist the new name, then refresh the list so
  // the label updates everywhere it's shown.
  async function renameSession(threadId: string, name: string): Promise<void> {
    await apiMutate(`/api/v1/agent/sessions/${encodeURIComponent(threadId)}`, {
      method: 'PATCH',
      body: { name },
      failureLabel: 'Failed to rename session',
    })
    await fetchSessions()
  }

  // Request cooperative cancellation: sets the flag on the agent box
  // so the streaming run's poll loop detects it and stops within CANCEL_POLL_SECS.
  async function cancelRun(): Promise<void> {
    const threadId = activeThreadId.value
    if (!threadId) return
    await apiMutate(`/api/v1/agent/sessions/${encodeURIComponent(threadId)}/cancel`, {
      method: 'POST',
      failureLabel: 'Failed to cancel run',
    })
  }

  // Delete a session: purge it brain-side, drop the open view if it was
  // the active one, then refresh the list.
  async function deleteSession(threadId: string): Promise<void> {
    await apiMutate(`/api/v1/agent/sessions/${encodeURIComponent(threadId)}`, {
      method: 'DELETE',
      failureLabel: 'Failed to delete session',
    })
    if (activeThreadId.value === threadId) newSession()
    await fetchSessions()
  }

  // Stop a session's container (#1355): frees its devcontainer slot (#1352)
  // without deleting the transcript — the session stays listed and reopening
  // it claims a fresh slot and clone. stoppingThreadId drives the per-row
  // pending state: the teardown runs a synchronous `devcontainer down`
  // (~10s), so without it the button gives no feedback and double-fires.
  const stoppingThreadId = ref<string | null>(null)

  async function stopSession(threadId: string): Promise<void> {
    stoppingThreadId.value = threadId
    try {
      await apiMutate(`/api/v1/agent/sessions/${encodeURIComponent(threadId)}/stop`, {
        method: 'POST',
        failureLabel: 'Failed to stop session',
      })
    } finally {
      stoppingThreadId.value = null
    }
  }

  const { subscribeStatus, subscribeSessionsList, lastNotified } = useAgentLiveSync({
    sessionsData,
    activeThreadId,
    runIndicator: { agentRunning, startRunning, stopRunning },
    fetchSessions,
  })

  const sessions = computed(() => sessionsData.value.sessions)
  const isEmpty = computed(() => loaded.value && !needsAuth.value && sessions.value.length === 0)

  return {
    sessions,
    loaded,
    loading,
    loadError,
    needsAuth,
    isEmpty,
    activeThreadId,
    messages,
    agentRunning,
    liveStatus,
    fetchSessions,
    openSession,
    startSession,
    sendMessage,
    newSession,
    cancelRun,
    renameSession,
    deleteSession,
    stopSession,
    stoppingThreadId,
    subscribeStatus,
    subscribeSessionsList,
    lastNotified,
  }
})
